import { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { FaChevronDown, FaArrowRight } from "react-icons/fa6";
import { AiOutlineUser } from "react-icons/ai";
import { TbMilitaryRank } from "react-icons/tb";
import { IoCalendarOutline } from "react-icons/io5";
import { MdOutlineAirplaneTicket } from "react-icons/md";
import { HiOutlinePencil } from "react-icons/hi";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";

import "./ApplyProcess.css";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = (d = 0) => ({ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease, delay: d } } });

const STEPS = [
  { num: 1, Icon: AiOutlineUser, label: "Identity", desc: "Tell us about who you are." },
  { num: 2, Icon: TbMilitaryRank, label: "Service", desc: "Share your service and experience." },
  { num: 3, Icon: IoCalendarOutline, label: "Availability", desc: "Let us know your availability." },
  { num: 4, Icon: MdOutlineAirplaneTicket, label: "Logistics", desc: "Travel, passport, and preferences." },
  { num: 5, Icon: HiOutlinePencil, label: "Your Story", desc: "Why now, and what you're hoping for." },
  { num: 6, Icon: IoMdCheckmarkCircleOutline, label: "Submit", desc: "Review and submit your application.", active: true },
];

const FAQS = [
  { q: "How long does the review process take?", a: "Most applications are reviewed within 5–7 business days. We'll reach out to schedule a confirmation call once your application is approved." },
  { q: "Do I need a passport?", a: "A passport is required for the Colombia retreat. If you don't have one, we can help guide you through the process — it won't stop your application." },
  { q: "What happens after I'm accepted?", a: "You'll receive a welcome packet and be paired with a team member who will help you prepare for every aspect of the retreat." },
  { q: "What are the room options?", a: "Accommodations at Cerro Tusa are shared private rooms designed for brotherhood and connection. Single occupancy may be available on request." },
  { q: "Can I bring a family member?", a: "The retreat is currently designed for individual participants. We are developing a family track — reach out if you'd like to be notified." },
  { q: "What if I need to cancel?", a: "Life happens. Contact us as soon as you know and we'll work with you to defer your spot to the next available retreat." },
];

function FaqItem({ q, a, delay, inView }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div className="apfaq__item" variants={fadeUp(delay)} initial="hidden" animate={inView ? "show" : "hidden"}>
      <button type="button" className="apfaq__q" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span>{q}</span>
        <FaChevronDown className="apfaq__chevron" style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div className="apfaq__a" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease }}>
            <p>{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function ApplyProcess({ onBegin }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <section className="aprc" ref={ref}>
      {/* ── ZONE 1: Application Process — full width ── */}
      <div className="aprc__process-zone">
        <div className="container-fg container-2xxl">
          <motion.div className="aprc__process-header" variants={fadeUp(0)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <h2 className="aprc__title">The Application Process</h2>
            <p className="aprc__sub">A guided, step-by-step application designed to understand your background, needs, and what you hope to gain from the retreat.</p>
          </motion.div>

          {/* Step cards — spread across full width */}
          <div className="aprc__steps">
            {STEPS.map((s, i) => (
              <div key={s.num} className="aprc__step-group">
                <motion.div
                  className={`aprc__step ${s.active ? "aprc__step--active" : ""}`}
                  variants={fadeUp(0.06 + i * 0.07)}
                  initial="hidden"
                  animate={inView ? "show" : "hidden"}
                  onClick={onBegin}
                  style={{ cursor: "pointer" }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && onBegin?.()}
                  aria-label={`Begin application — ${s.label}`}
                >
                  <div className="aprc__step-icon" aria-hidden="true">
                    <s.Icon size={45} />
                  </div>
                  <div className="aprc__step-content">
                    <div className="aprc__step-num-label">
                      <span className="aprc__step-num">{s.num}</span>
                      <span className="aprc__step-label">{s.label.toUpperCase()}</span>
                    </div>
                    <p className="aprc__step-desc">{s.desc}</p>
                  </div>
                  <FaArrowRight className="aprc__step-arrow" aria-hidden="true" />
                </motion.div>

                {i < STEPS.length - 1 && (
                  <motion.span className="aprc__arrow" aria-hidden="true" variants={fadeUp(0.1 + i * 0.07)} initial="hidden" animate={inView ? "show" : "hidden"}>
                    <FaArrowRight />
                  </motion.span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── ZONE 2: Common Questions — full width, distinct bg ── */}
      <div className="aprc__faq-zone">
        <div className="container-fg container-2xxl">
          {/* FAQ header row with rules */}
          <motion.div className="aprc__faq-header" variants={fadeUp(0.05)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <span className="aprc__faq-rule" aria-hidden="true" />
            <h2 className="aprc__faq-heading u-eyebrow">Common Questions</h2>
            <span className="aprc__faq-rule" aria-hidden="true" />
          </motion.div>

          {/* Two-column FAQ grid */}
          <div className="aprc__faq-grid">
            {FAQS.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} delay={0.08 + i * 0.06} inView={inView} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
